import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useApp } from "../../context/AppContext";
import employeeService from "../../services/employeeService";
import employeeAttendanceService from "../../services/employeeAttendanceService";

const STATUS_OPTIONS = [
  "Present",
  "Absent",
  "Late",
  "On Leave",
  "Medical Leave",
  "Half Day",
];

const STATUS_STYLES = {
  Present: { bg: "#dcfce7", color: "#166534" },
  Absent: { bg: "#fee2e2", color: "#b91c1c" },
  Late: { bg: "#fef3c7", color: "#b45309" },
  "On Leave": { bg: "#e0f2fe", color: "#0369a1" },
  "Medical Leave": { bg: "#ede9fe", color: "#6d28d9" },
  "Half Day": { bg: "#fce7f3", color: "#be185d" },
};

const getToday = () => new Date().toISOString().slice(0, 10);

export default function EmployeeMarkAttendance() {
  const { user } = useApp();
  const [date, setDate] = useState(getToday);
  const [department, setDepartment] = useState("");
  const [designation, setDesignation] = useState("");
  const [statusFilter, setStatusFilter] = useState("Active");
  const [search, setSearch] = useState("");
  const [employees, setEmployees] = useState([]);
  const [attendanceMap, setAttendanceMap] = useState({});
  const [markedBy, setMarkedBy] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [designationOptions, setDesignationOptions] = useState([]);

  useEffect(() => {
    setMarkedBy(user?.displayName || user?.username || "");
  }, [user]);

  useEffect(() => {
    employeeService
      .getAll({ limit: 500 })
      .then((res) => {
        const rows = res.data || [];
        const departments = [...new Set(rows.map((item) => item.department).filter(Boolean))];
        const designations = [...new Set(rows.map((item) => item.designation).filter(Boolean))];

        setDepartmentOptions(departments);
        setDesignationOptions(designations);
        setDepartment((current) => current || departments[0] || "");
      })
      .catch((err) => toast.error(err.message));
  }, []);

  useEffect(() => {
    if (!date) return;

    setLoading(true);
    employeeAttendanceService
      .getMarkingSheet({
        attendanceDate: date,
        department,
        designation,
        status: statusFilter,
        search,
      })
      .then((res) => {
        const rows = res.data || [];
        setEmployees(rows);
        setMarkedBy((current) => res.meta?.markedBy || current || user?.displayName || "");
        setNotes(res.meta?.notes || "");
        setUpdatedAt(res.meta?.updatedAt || null);

        const nextMap = {};
        rows.forEach((employee) => {
          nextMap[employee._id] = {
            status: employee.attendance?.status || "",
            remarks: employee.attendance?.remarks || "",
          };
        });
        setAttendanceMap(nextMap);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  }, [date, department, designation, statusFilter, search, user]);

  const handleStatusChange = (employeeId, value) => {
    setAttendanceMap((prev) => ({
      ...prev,
      [employeeId]: {
        ...(prev[employeeId] || { remarks: "" }),
        status: value,
      },
    }));
  };

  const handleRemarksChange = (employeeId, value) => {
    setAttendanceMap((prev) => ({
      ...prev,
      [employeeId]: {
        ...(prev[employeeId] || { status: "" }),
        remarks: value,
      },
    }));
  };

  const applyBulkStatus = (status) => {
    setAttendanceMap((prev) => {
      const next = { ...prev };
      employees.forEach((employee) => {
        next[employee._id] = {
          ...(next[employee._id] || { remarks: "" }),
          status,
        };
      });
      return next;
    });
  };

  const summary = useMemo(() => {
    const counts = STATUS_OPTIONS.reduce(
      (acc, status) => ({ ...acc, [status]: 0 }),
      {},
    );

    let unmarked = 0;
    employees.forEach((employee) => {
      const status = attendanceMap[employee._id]?.status || "";
      if (!status) unmarked += 1;
      else counts[status] += 1;
    });

    return { counts, unmarked };
  }, [attendanceMap, employees]);

  const handleSave = async () => {
    const records = employees
      .map((employee) => ({
        employee: employee._id,
        status: attendanceMap[employee._id]?.status || "",
        remarks: attendanceMap[employee._id]?.remarks || "",
      }))
      .filter((record) => record.status);

    if (!records.length) {
      toast.error("Please mark attendance for at least one employee.");
      return;
    }

    setSaving(true);
    try {
      await employeeAttendanceService.markAttendance({
        attendanceDate: date,
        markedBy,
        notes,
        records,
      });
      setUpdatedAt(new Date().toISOString());
      toast.success("Employee attendance saved successfully.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="page-header" style={{ flexWrap: "wrap", gap: "0.75rem" }}>
        <div>
          <h1 className="page-title">Add Attendance</h1>
          <p className="page-subtitle">Mark daily attendance for employees.</p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          <button type="button" className="btn btn-outline" onClick={() => applyBulkStatus("Present")}>
            Mark All Present
          </button>
          <button type="button" className="btn btn-outline" onClick={() => applyBulkStatus("Absent")}>
            Mark All Absent
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save Attendance"}
          </button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: "1rem" }}>
        <div className="card-body">
          <div className="form-grid" style={{ alignItems: "end" }}>
            <div className="form-group">
              <label className="form-label">Attendance Date</label>
              <input className="form-control" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Department</label>
              <select className="form-control" value={department} onChange={(e) => setDepartment(e.target.value)}>
                <option value="">All Departments</option>
                {departmentOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Designation</label>
              <select className="form-control" value={designation} onChange={(e) => setDesignation(e.target.value)}>
                <option value="">All Designations</option>
                {designationOptions.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Employee Status</label>
              <select className="form-control" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All Statuses</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
                <option value="On Leave">On Leave</option>
                <option value="Resigned">Resigned</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Marked By</label>
              <input className="form-control" value={markedBy} onChange={(e) => setMarkedBy(e.target.value)} placeholder="Admin / HR name" />
            </div>
            <div className="form-group">
              <label className="form-label">Search Employee</label>
              <input className="form-control" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Name, code, mobile..." />
            </div>
            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
              <label className="form-label">Notes</label>
              <textarea className="form-control" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional note for the day" />
            </div>
          </div>
          {updatedAt && (
            <div style={{ marginTop: "0.75rem", fontSize: 12, color: "var(--text-muted)" }}>
              Last updated: {new Date(updatedAt).toLocaleString("en-IN")}
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "0.75rem", marginBottom: "1rem" }}>
        <div className="card"><div className="card-body" style={{ padding: "1rem" }}><div style={{ fontSize: 12, color: "var(--text-muted)" }}>Employees Loaded</div><div style={{ fontSize: 24, fontWeight: 700 }}>{employees.length}</div></div></div>
        <div className="card"><div className="card-body" style={{ padding: "1rem" }}><div style={{ fontSize: 12, color: "var(--text-muted)" }}>Unmarked</div><div style={{ fontSize: 24, fontWeight: 700, color: "#b45309" }}>{summary.unmarked}</div></div></div>
        {STATUS_OPTIONS.map((status) => (
          <div key={status} className="card">
            <div className="card-body" style={{ padding: "1rem" }}>
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{status}</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: STATUS_STYLES[status].color }}>{summary.counts[status]}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="card">
        {loading ? (
          <div className="loading-center"><div className="spinner" />Loading attendance sheet...</div>
        ) : employees.length === 0 ? (
          <div className="empty-state">
            <p>No employees found for the selected filters.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Employee</th>
                  <th>Code</th>
                  <th>Department</th>
                  <th>Status</th>
                  <th>Attendance</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {employees.map((employee, index) => {
                  const status = attendanceMap[employee._id]?.status || "";
                  const style = STATUS_STYLES[status];

                  return (
                    <tr key={employee._id}>
                      <td>{index + 1}</td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{employee.fullName}</div>
                        <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                          {employee.designation}
                        </div>
                      </td>
                      <td><span className="badge badge-blue">{employee.employeeCode}</span></td>
                      <td>{employee.department}</td>
                      <td>{employee.status}</td>
                      <td style={{ minWidth: 180 }}>
                        <select
                          className="form-control"
                          value={status}
                          onChange={(e) => handleStatusChange(employee._id, e.target.value)}
                          style={style ? { background: style.bg, color: style.color, borderColor: style.bg, fontWeight: 600 } : undefined}
                        >
                          <option value="">Select Status</option>
                          {STATUS_OPTIONS.map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </td>
                      <td style={{ minWidth: 220 }}>
                        <input
                          className="form-control"
                          value={attendanceMap[employee._id]?.remarks || ""}
                          onChange={(e) => handleRemarksChange(employee._id, e.target.value)}
                          placeholder="Optional note"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
